import { formatTicker } from '@api/mappers/formatTicker';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { THEME_TAG_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { Property } from 'csstype';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Placeholder,
  ReportingDateCell,
  SignedPercentage,
} from './TopCompetitorCells';
import styles from './TopCompetitors.module.scss';
import { CompetitorRow } from './useTopCompetitorsData';

export interface TopCompetitorColumn {
  key: string;
  label: string;
  alignment: Property.TextAlign;
  className?: string;
  cell: (row: CompetitorRow) => ReactNode;
}

export const useTopCompetitorColumns = (): TopCompetitorColumn[] => {
  const { t } = useTranslation(['earnings']);

  return [
    {
      key: 'rank',
      label: t('earnings:peerComparison.topCompetitors.columns.rank'),
      alignment: 'start',
      className: styles.rankColumn,
      cell: ({ rank }) => <TopRankIcon rank={rank} />,
    },
    {
      key: 'company',
      label: t('earnings:peerComparison.topCompetitors.columns.company'),
      alignment: 'start',
      className: styles.companyColumn,
      cell: ({ entity }) => {
        const name = entity.name_short || entity.name;

        return (
          <span className={styles.company}>
            <AssetLogoContainer entity={entity} size={28} />
            <span className={styles.companyText}>
              <span className={styles.companyName}>{name}</span>
              <span className={styles.companyTicker}>
                {formatTicker(entity)}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      key: 'reportingDate',
      label: t('earnings:peerComparison.topCompetitors.columns.reportingDate'),
      alignment: 'start',
      cell: ({ earnings }) => (
        <ReportingDateCell
          reportingDate={earnings.reportingDate}
          hasReported={earnings.hasReported}
        />
      ),
    },
    {
      key: 'epsSurprise',
      label: t('earnings:peerComparison.topCompetitors.columns.epsSurprise'),
      alignment: 'end',
      cell: ({ earnings }) => (
        <SignedPercentage value={earnings.epsSurprisePct} />
      ),
    },
    {
      key: 'revenueSurprise',
      label: t(
        'earnings:peerComparison.topCompetitors.columns.revenueSurprise'
      ),
      alignment: 'end',
      cell: ({ earnings }) => (
        <SignedPercentage value={earnings.revenueSurprisePct} />
      ),
    },
    {
      key: 'tPlus1',
      label: t('earnings:1t'),
      alignment: 'end',
      cell: ({ earnings }) => <SignedPercentage value={earnings.tPlus1Pct} />,
    },
    {
      key: 'tPlus2',
      label: t('earnings:2t'),
      alignment: 'end',
      cell: ({ earnings }) => <SignedPercentage value={earnings.tPlus2Pct} />,
    },
    {
      key: 'leadingThemeDriver',
      label: t(
        'earnings:peerComparison.topCompetitors.columns.leadingThemeDriver'
      ),
      alignment: 'end',
      cell: ({ leadingTheme }) =>
        leadingTheme ? (
          <span className={THEME_TAG_COLOR_CLASS_MAP[leadingTheme.themeType]}>
            {leadingTheme.label}
          </span>
        ) : (
          <Placeholder />
        ),
    },
  ];
};
